import React, { useState, useRef, useEffect } from 'react';
import {
  Textarea,
  Button,
  ScrollArea,
  Paper,
  Text,
  Loader,
  Alert,
  Group,
  Divider,
  Badge,
  Table,
  Stack,
  useMantineTheme,
  ActionIcon,
  Tooltip,
  Tabs,
  SimpleGrid,
  Card,
  ThemeIcon,
  Drawer,
  Modal,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { Trash, Plus, History, ChevronLeft, MessageCircle, ChevronRight, InfoCircle, Send, Menu2 } from 'tabler-icons-react';
import { IconPhoto, IconX } from '@tabler/icons-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { doc, getDoc, collection } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import useAIChat, { ChatAttachment } from '../../hooks/useAIChat';
import { downscaleImageIfNeeded, formatFileSize, isAllowedImageType, ALLOWED_IMAGE_TYPES } from '../../utils/image-compression';
import { saveGeneratedHandlebarsTemplates } from './hooks/template-functions';
import { OptionsSource } from "@captiva/shared-types";
import { useNavigate } from 'react-router-dom';

const TabItem = Tabs.Tab as unknown as React.ComponentType<{
  children?: React.ReactNode;
  label: string;
  color?: string;
}>;

const QUICK_PROMPTS = [
  'Fuel trend summary for this week.',
  'Top anomalies from recent fuel transactions.',
  'Create a pre-start checklist form template for farm equipment.',
  'Tell me about my most recent trips.',
  'Show me user access levels',
  'Create a new vehicle: Toyota Hilux, White 2022, ODO 34084 km',
];

export interface ChatField {
  id?: string;
  key?: string;
  label?: string;
  type?: string;
  required?: boolean;
  optionsSource?: OptionsSource;
  checklistNoteInputType?: string;
}

export interface ChatCitation {
  id?: string;
  href: string;
  label: string;
  imageUrl?: string;
}

export interface ChatMessage {
  role: 'user' | 'model';
  createdAt?: number;
  type?: string;
  text?: string;
  citations?: ChatCitation[];
  domain?: string;
  skill?: string;
  reasoning?: string;
  data?: {
    fields?: ChatField[];
    [key: string]: any;
  };
  changedFields?: string[];
  confirmAction?: string;
  confirmPayload?: any;
  options?: string[];
  actionTaken?: 'accepted' | 'rejected';
  actionAssetId?: string;
  attachments?: ChatAttachment[];
}

export interface StagedAttachment {
  file: File | Blob;
  name: string;
  originalSize: number;
  compressedSize: number;
  mimeType: string;
  previewUrl: string;
  wasDownscaled: boolean;
}

export interface ChatBoxProps {
  companyId: string;
  currentPath?: string;
  mode?: 'lite' | 'full';
  titleOverride?: React.ReactNode;
  subtitleOverride?: React.ReactNode;
  extraAction?: React.ReactNode;
}

function humanizeLabel(value: string | null | undefined): string {
  return String(value || 'Asset')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (character) => character.toUpperCase())
    .trim();
}

function renderSuggestionValue(value: unknown, depth = 0): React.ReactNode {
  if (value === null || value === undefined || value === '') {
    return <Text size="sm" color="dimmed">-</Text>;
  }

  if (typeof value === 'boolean') {
    return <Text size="sm">{value ? 'true' : 'false'}</Text>;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return <Text size="sm">{String(value)}</Text>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <Text size="sm" color="dimmed">Empty</Text>;
    }

    if (depth >= 2) {
      return <Text size="sm">{`${value.length} items`}</Text>;
    }

    return (
      <Stack spacing={4}>
        {value.map((item: unknown, index) => (
          <Paper
            key={`${index}-${typeof item === 'object' ? JSON.stringify(item) : String(item)}`}
            p={6}
            style={{
              background: '#fff',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
            }}
          >
            {typeof item === 'object' && item !== null ? (
              <Stack spacing={2}>
                {Object.entries(item).map(([childKey, childValue]) => (
                  <Group key={childKey} align="flex-start" spacing={6} noWrap>
                    <Text size="sm" weight={600} style={{ minWidth: '88px' }}>
                      {humanizeLabel(childKey)}:
                    </Text>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      {renderSuggestionValue(childValue, depth + 1)}
                    </div>
                  </Group>
                ))}
              </Stack>
            ) : (
              <Text size="sm">{String(item)}</Text>
            )}
          </Paper>
        ))}
      </Stack>
    );
  }

  if (typeof value === 'object' && value !== null) {
    const entries = Object.entries(value);

    if (entries.length === 0) {
      return <Text size="sm" color="dimmed">Empty</Text>;
    }

    return (
      <Stack spacing={2}>
        {entries.map(([childKey, childValue]) => (
          <Group key={childKey} align="flex-start" spacing={6} noWrap>
            <Text size="sm" weight={600} style={{ minWidth: '88px' }}>
              {humanizeLabel(childKey)}:
            </Text>
            <div style={{ minWidth: 0, flex: 1 }}>
              {renderSuggestionValue(childValue, depth + 1)}
            </div>
          </Group>
        ))}
      </Stack>
    );
  }

  return <Text size="sm">{String(value)}</Text>;
}

function renderFieldTypeDetails(field: ChatField): string {
  const details = [];

  if (field.optionsSource?.type === 'firestore') {
    details.push(`options: linked set${field.optionsSource.optionsSetId ? ` (${field.optionsSource.optionsSetId})` : ''}`);
  }

  if (field.optionsSource?.type === 'static' && Array.isArray(field.optionsSource.options)) {
    details.push(`options: ${field.optionsSource.options.length} static`);
  }

  if (field.checklistNoteInputType) {
    details.push(`notes: ${field.checklistNoteInputType}`);
  }

  return details.join(' | ');
}

function renderFieldsTable(fields: ChatField[]): React.ReactNode {
  if (!Array.isArray(fields) || fields.length === 0) {
    return <Text size="sm" color="dimmed">No fields</Text>;
  }

  return (
    <Table striped style={{ fontSize: '14px' }}>
      <thead>
        <tr>
          <th style={{ width: '36px' }}>#</th>
          <th>Label</th>
          <th>Type</th>
          <th style={{ width: '60px' }}>Req</th>
          <th>Details</th>
        </tr>
      </thead>
      <tbody>
        {fields.map((field, index) => {
          const details = renderFieldTypeDetails(field);

          return (
            <tr key={field.id || field.key || `${field.label || ''}-${index}`}>
              <td>
                <Text size="sm">{index + 1}</Text>
              </td>
              <td>
                <Stack spacing={2}>
                  <Text size="sm" weight={600}>{field.label || '-'}</Text>
                  {(field.key || field.id) && (
                    <Text size="sm" color="dimmed">
                      {[field.key, field.id].filter(Boolean).join(' | ')}
                    </Text>
                  )}
                </Stack>
              </td>
              <td>
                <Badge size="xs" variant="light" color="cap-blue">
                  {humanizeLabel(field.type || 'text')}
                </Badge>
              </td>
              <td>
                <Text size="sm">{(field.required ?? (field as any).validation?.required) ? 'Yes' : 'No'}</Text>
              </td>
              <td>
                {details ? <Text size="sm">{details}</Text> : <Text size="sm" color="dimmed">-</Text>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}

const ChatBox = ({ companyId, currentPath = window.location.pathname, mode = 'full', titleOverride, subtitleOverride, extraAction }: ChatBoxProps) => {
  const navigate = useNavigate();
  const theme = useMantineTheme();

  // Context detection based on currentPath
  const isFuel = currentPath.includes('Refuel') || currentPath.includes('Tank') || currentPath.includes('Transfer') || currentPath.includes('Bulk') || currentPath.includes('fuel-configs') || currentPath.includes('reports');
  const isFlex = currentPath.includes('form-') || currentPath.includes('flex-');
  const isTrip = currentPath.includes('trips');
  const isAssets = currentPath.includes('assets') || currentPath.includes('users');

  const getContextualSuggestions = () => {
    if (isFuel) {
      return {
        title: "Fuel Operations Co-pilot",
        suggestions: [
          { text: "What is the current fuel level in Tank A?", desc: "Check current asset status" },
          { text: "Tell me about my most recent fuel transactions.", desc: "Analyze metrics and logs" },
          { text: "Top anomalies from recent fuel transactions.", desc: "Scan recent logs for potential input errors" },
        ]
      };
    }
    if (isFlex) {
      return {
        title: "Captiva Flex Co-pilot",
        suggestions: [
          { text: "Show me the latest quantities reported for Pre-em Maize.", desc: "Query specific submitted data" },
          { text: "Summarize recent form submissions for Pre-start Checklist.", desc: "Analyze form submissions" },
          { text: "Create a pre-start checklist form template for farm equipment.", desc: "Create new forms instantly using AI" },
        ]
      };
    }
    if (isTrip) {
      return {
        title: "Captiva Trip Co-pilot",
        suggestions: [
          { text: "Tell me about my most recent trips, including what pictures were taken.", desc: "Scan route inspection delays and media" },
          { text: "Find delay hot-spots in trips", desc: "Identify geofence crossing delay trends" },
          { text: "What is the pre-trip inspection workflow gate?", desc: "Learn workflow gates rules" },
        ]
      };
    }
    if (isAssets) {
      return {
        title: "Assets Co-pilot",
        suggestions: [
          { text: "Show me user access levels", desc: "Check system permissions and access" },
          { text: "Create a new vehicle: Toyota Hilux, White 2022, ODO 34084 km", desc: "Onboard fleet assets instantly" },
          { text: "Create a new operator: John Doe, Code J01", desc: "Create operator accounts" },
        ]
      };
    }

    // Default overview
    return {
      title: "Recommended for you",
      suggestions: [
        { text: "Tell me about my most recent trips.", desc: "Analyze metrics and logs" },
        { text: "Create a pre-start checklist form template for farm equipment.", desc: "Generate a Flex Form layout" },
        { text: "Create a new vehicle: Toyota Hilux, White 2022, ODO 34084 km", desc: "Onboard fleet assets instantly" },
      ]
    };
  };
  const isBelowLarge = useMediaQuery('(max-width: 850px)');
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isGeneratingTemplates, setIsGeneratingTemplates] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [lastSubmittedPrompt, setLastSubmittedPrompt] = useState('');
  const [lastSubmittedAttachments, setLastSubmittedAttachments] = useState<ChatAttachment[] | undefined>(undefined);
  const [pendingSkill, setPendingSkill] = useState<string | null>(null);

  // Multimodal Attachment State
  const [stagedAttachment, setStagedAttachment] = useState<StagedAttachment | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const isSendingRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Preview Modal State
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');
  const [previewImageTitle, setPreviewImageTitle] = useState<string>('');

  // Active Image Context state & derivation
  const [clearedAttachmentUrls, setClearedAttachmentUrls] = useState<string[]>([]);

  const activeImageContext = React.useMemo<ChatAttachment | null>(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const atts = messages[i].attachments;
      if (atts && atts.length > 0) {
        const found = atts.find((a) => !clearedAttachmentUrls.includes(a.url));
        if (found) return found;
      }
    }
    return null;
  }, [messages, clearedAttachmentUrls]);

  const handleClearActiveImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (activeImageContext) {
      setClearedAttachmentUrls((prev) => [...prev, activeImageContext.url]);
    }
  };

  const [conversations, setConversations] = useState<any[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingActiveConversation, setLoadingActiveConversation] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const {
    sendMessage,
    confirmAction,
    listConversations,
    getConversationMessages,
    deleteConversation,
    loading,
    error,
  } = useAIChat();

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const isThinking = loading || isGeneratingTemplates || isCompressing || isUploading;

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      if (stagedAttachment?.previewUrl) {
        try {
          URL.revokeObjectURL(stagedAttachment.previewUrl);
        } catch {}
      }
    };
  }, [stagedAttachment]);

  const handleRemoveAttachment = () => {
    if (stagedAttachment?.previewUrl) {
      try {
        URL.revokeObjectURL(stagedAttachment.previewUrl);
      } catch {}
    }
    setStagedAttachment(null);
    setAttachmentError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setAttachmentError(null);

    // Strict validation: reject SVG and unsupported MIME types
    if (!isAllowedImageType(file.type, file.name)) {
      setAttachmentError('SVG and unsupported file types are strictly prohibited. Please select a JPEG, PNG, WebP, or HEIC/HEIF photo.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Clean up previous staged attachment preview URL if replacing
    if (stagedAttachment?.previewUrl) {
      try {
        URL.revokeObjectURL(stagedAttachment.previewUrl);
      } catch {}
    }

    setIsCompressing(true);
    try {
      const compressionResult = await downscaleImageIfNeeded(file);
      const previewUrl = URL.createObjectURL(compressionResult.blob);

      setStagedAttachment({
        file: compressionResult.blob,
        name: file.name,
        originalSize: file.size,
        compressedSize: compressionResult.blob.size,
        mimeType: compressionResult.mimeType,
        previewUrl,
        wasDownscaled: compressionResult.wasDownscaled,
      });
    } catch (err: any) {
      setAttachmentError(err?.message || 'Failed to process selected image.');
    } finally {
      setIsCompressing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const loadConversationsList = async () => {
    setLoadingConversations(true);
    try {
      const list = await listConversations();
      setConversations(list || []);
    } catch (err) {
      console.error('Error loading conversations', err);
    } finally {
      setLoadingConversations(false);
    }
  };

  const loadConversation = async (id: string) => {
    setLoadingActiveConversation(true);
    setConversationId(id);
    setPendingSkill(null);
    setShowHistory(false);
    setClearedAttachmentUrls([]);
    try {
      const msgs = await getConversationMessages(id);
      const mapped: ChatMessage[] = (msgs || []).map((m: any) => ({
        role: m.role,
        text: m.text,
        createdAt: m.timestamp,
        citations: [],
        attachments: m.attachments,
        ...(m.type ? { type: m.type } : {}),
        ...(m.domain ? { domain: m.domain } : {}),
        ...(m.skill ? { skill: m.skill } : {}),
        ...(m.reasoning ? { reasoning: m.reasoning } : {}),
        ...(m.data ? { data: m.data } : {}),
        ...(m.changedFields ? { changedFields: m.changedFields } : {}),
        ...(m.confirmAction ? { confirmAction: m.confirmAction } : {}),
        ...(m.confirmPayload ? { confirmPayload: m.confirmPayload } : {}),
        ...(m.options ? { options: m.options } : {}),
        ...(m.actionTaken ? { actionTaken: m.actionTaken } : {}),
        ...(m.actionAssetId ? { actionAssetId: m.actionAssetId } : {}),
      }));
      setMessages(mapped);

      const lastMsg = Array.isArray(msgs) && msgs.length > 0 ? msgs[msgs.length - 1] : null;
      if (lastMsg?.pendingSkill && !lastMsg?.actionTaken) {
        setPendingSkill(lastMsg.pendingSkill);
      }
    } catch (err) {
      console.error('Error loading conversation messages', err);
    } finally {
      setLoadingActiveConversation(false);
    }
  };

  const handleDeleteConversation = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await deleteConversation(id);
      if (conversationId === id) {
        setConversationId(null);
        setMessages([]);
        setClearedAttachmentUrls([]);
      }
      await loadConversationsList();
    } catch (err) {
      console.error('Error deleting conversation', err);
    }
  };

  const startNewChat = () => {
    setConversationId(null);
    setMessages([]);
    setPendingSkill(null);
    setShowHistory(false);
    setClearedAttachmentUrls([]);
  };

  useEffect(() => {
    loadConversationsList();
  }, [conversationId]);

  const formatDate = (timestampMs: number) => {
    if (!timestampMs) return '';
    const date = new Date(timestampMs);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getDynamicPrompts = () => {
    const dynamicPrompts: string[] = [];
    const seen = new Set<string>();

    for (const conv of conversations) {
      if (conv.initialPrompt) {
        const trimmed = conv.initialPrompt.trim();
        if (trimmed && !seen.has(trimmed)) {
          seen.add(trimmed);
          dynamicPrompts.push(trimmed);
          if (dynamicPrompts.length >= 3) break;
        }
      }
    }

    const staticPrompts = QUICK_PROMPTS.filter((p) => !seen.has(p));
    const combined = [...dynamicPrompts, ...staticPrompts];
    return combined.slice(0, 6);
  };

  const handleSend = async (messageText = input, retryAttachments?: ChatAttachment[]) => {
    if (isSendingRef.current || isThinking || isUploading) return;

    const DOMAIN_NEUTRAL_PROMPT =
      'Analyze this image and provide relevant details, readings, or insights based on our company data.';
    const rawTrimmed = messageText.trim();

    // Intercept explicit conversational closure commands
    if (/^(RESET|DONE|NEW CHAT|NEW|CLEAR|\/reset)$/i.test(rawTrimmed)) {
      startNewChat();
      setInput('');
      handleRemoveAttachment();
      return;
    }

    const hasAttachment = Boolean(stagedAttachment) || Boolean(retryAttachments && retryAttachments.length > 0);

    if (!rawTrimmed && !hasAttachment) return;

    isSendingRef.current = true;
    setIsUploading(true);

    const trimmedMessage = rawTrimmed || DOMAIN_NEUTRAL_PROMPT;
    const activePendingSkill = pendingSkill;

    // Active conversation ID resolution
    const activeConvId =
      conversationId || `conv-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    if (!conversationId) setConversationId(activeConvId);

    try {
      // Storage upload if attachment is staged and not a retry with already-uploaded attachments
      let uploadedAttachment: ChatAttachment | undefined;
      if (retryAttachments && retryAttachments.length > 0) {
        uploadedAttachment = retryAttachments[0];
      } else if (stagedAttachment) {
        try {
          const ext = stagedAttachment.wasDownscaled
            ? 'jpg'
            : (stagedAttachment.name.split('.').pop() || 'jpg');
          const fileId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}.${ext}`;
          const storagePath = `${companyId}/chat/${activeConvId}/${fileId}`;
          const storageRef = ref(storage, storagePath);

          const snapshot = await uploadBytes(storageRef, stagedAttachment.file, {
            contentType: stagedAttachment.mimeType,
            customMetadata: {
              companyId,
              originalName: stagedAttachment.name,
            },
          });

          const downloadUrl = await getDownloadURL(snapshot.ref);

          uploadedAttachment = {
            url: downloadUrl,
            mimeType: stagedAttachment.mimeType,
            storagePath,
            name: stagedAttachment.name,
            size: stagedAttachment.compressedSize,
          };
        } catch (uploadErr: any) {
          console.error('Failed to upload chat image to storage:', uploadErr);
          setAttachmentError('Failed to upload image. Please try again.');
          return;
        }
      }

      const userAttachments = uploadedAttachment ? [uploadedAttachment] : undefined;
      const userMessage: ChatMessage = {
        role: 'user',
        text: trimmedMessage,
        createdAt: Date.now(),
        attachments: userAttachments,
      };
      setMessages(prev => [...prev, userMessage]);
      setInput('');
      setLastSubmittedPrompt(trimmedMessage);
      setLastSubmittedAttachments(userAttachments);
      handleRemoveAttachment();

      const contextPayload: Record<string, unknown> = {};
      if (activePendingSkill) {
        contextPayload.pendingSkill = activePendingSkill;
      }
      if (clearedAttachmentUrls.length > 0) {
        contextPayload.clearedAttachmentUrls = clearedAttachmentUrls;
      }
      if (activeImageContext?.storagePath) {
        contextPayload.activeImageStoragePath = activeImageContext.storagePath;
      }
      const context = Object.keys(contextPayload).length > 0 ? contextPayload : undefined;
      const result = await sendMessage(trimmedMessage, companyId, activeConvId, {
        context,
        useMultimodal: true,
        attachments: userAttachments,
      });
      setPendingSkill(null);

      if (result.type === 'answer') {
        const aiMessage: ChatMessage = {
          role: 'model',
          text: result.text,
          citations: Array.isArray(result.citations) ? result.citations : [],
          createdAt: Date.now(),
        };
        setMessages(prev => [...prev, aiMessage]);
        if (result.conversationId) setConversationId(result.conversationId);
      } else if (result.type === 'suggestion') {
        const suggestionMessage: ChatMessage = {
          role: 'model',
          type: 'suggestion',
          domain: result.domain,
          skill: result.skill,
          reasoning: result.reasoning,
          data: result.data,
          changedFields: result.changedFields,
          confirmAction: result.confirmAction,
          confirmPayload: result.confirmPayload,
          createdAt: Date.now(),
        };
        setMessages(prev => [...prev, suggestionMessage]);
        if (result.conversationId) setConversationId(result.conversationId);
        if (result.pendingSkill) setPendingSkill(result.pendingSkill);
      } else if (result.type === 'clarification') {
        const clarifyMessage: ChatMessage = {
          role: 'model',
          type: 'clarification',
          text: result.text,
          options: result.options,
          createdAt: Date.now(),
        };
        setMessages(prev => [...prev, clarifyMessage]);
        if (result.conversationId) setConversationId(result.conversationId);
        if (result.pendingSkill) setPendingSkill(result.pendingSkill);
      } else if (result.type === 'error') {
        const errorMessage: ChatMessage = { role: 'model', text: result.message, createdAt: Date.now() };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (err) {
      // Error handled in hook
    } finally {
      isSendingRef.current = false;
      setIsUploading(false);
    }
  };

  const handleConfirm = async (msg: ChatMessage) => {
    try {
      setIsGeneratingTemplates(true);
      const result = await confirmAction(msg.confirmPayload);

      if (
        msg.domain === 'formTemplate' &&
        msg.confirmPayload?.confirmData?.action === 'create' &&
        result.assetId &&
        Array.isArray(msg.data?.fields)
      ) {
        try {
          await saveGeneratedHandlebarsTemplates({
            templateId: result.assetId,
            companyId,
            fields: msg.data.fields,
          });
        } catch (templateError) {
          console.error('Error generating template configs after form template creation:', templateError);
          setMessages(prev => [...prev, {
            role: 'model',
            text: 'Form template created, but generating notification and AI story templates failed. You can still configure them manually in the template editor.',
            createdAt: Date.now(),
          }]);
        }
      }

      setPendingSkill(null);
      const action = msg.confirmPayload?.confirmData?.action;
      const actionLabel = action === 'create' ? 'created' : 'updated';
      const successText = result.text || `${humanizeLabel(msg.domain)} ${actionLabel} successfully.`;
      setMessages(prev => {
        const updated = prev.map(m => m === msg ? { ...m, actionTaken: 'accepted' as const, actionAssetId: result.assetId } : m);
        return [...updated, { 
          role: 'model', 
          text: successText, 
          createdAt: Date.now(),
          actionAssetId: result.assetId,
          domain: msg.domain
        }];
      });
    } catch (err) {
      // Error handled in hook
    } finally {
      setIsGeneratingTemplates(false);
    }
  };

  const handleReject = (msg: ChatMessage) => {
    setPendingSkill(null);
    setMessages(prev => {
      const updated = prev.map(m => m === msg ? { ...m, actionTaken: 'rejected' as const } : m);
      return [...updated, {
        role: 'model',
        text: `Suggestion dismissed. You can refine your request or try again.`,
        createdAt: Date.now(),
      }];
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !loading) {
      e.preventDefault();
      handleSend();
    }
  };

  useEffect(() => {
    if (scrollAreaRef.current) {
      const timeoutId = setTimeout(() => {
        if (scrollAreaRef.current) {
          scrollAreaRef.current.scrollTo({ top: scrollAreaRef.current.scrollHeight, behavior: 'smooth' });
        }
      }, 150);
      return () => clearTimeout(timeoutId);
    }
  }, [messages, isThinking]);

  const handleRetry = () => {
    if (!lastSubmittedPrompt || loading) return;
    handleSend(lastSubmittedPrompt, lastSubmittedAttachments);
  };

  const applyQuickPrompt = (prompt: string) => {
    if (loading) return;
    setInput(prompt);
  };

  const cardBorder = theme.colors.gray[3];
  const softSurface = theme.colors.gray[0];
  const subtleBorder = theme.colors.gray[2];
  const userBubbleBg = theme.colors['cap-purple'][0];
  const userBubbleBorder = theme.colors['cap-purple'][2];
  const aiBubbleBg = theme.colors.gray[0];
  const aiBubbleBorder = theme.colors.gray[3];
  const respondingBg = theme.colors['cap-orange'][0];
  const respondingBorder = theme.colors['cap-orange'][2];
  const respondingDot = theme.colors['cap-orange'][6];
  const respondingText = theme.colors['cap-orange'][9];
  const readyBg = theme.fn.rgba(theme.colors['cap-green'][1], 0.08);
  const readyBorder = theme.fn.rgba(theme.colors['cap-green'][1], 0.2);
  const readyDot = theme.colors['cap-green'][1];
  const readyText = theme.colors.gray[7];

  const paperStyle = {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: '400px',
    borderRadius: isBelowLarge || mode === 'full' ? 0 : '14px',
    border: isBelowLarge || mode === 'full' ? 'none' : `1px solid ${cardBorder}`,
    padding: isBelowLarge || mode === 'full' ? 0 : theme.spacing.sm,
    background: isBelowLarge || mode === 'full' ? 'transparent' : undefined,
  };

  return (
    <Paper
      shadow={isBelowLarge || mode === 'full' ? 'none' : 'sm'}
      style={paperStyle}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
          handleFileSelect({ target: { files } } as any);
        }
      }}
    >
      <Group position="apart" mb="xs">
        <Group spacing="xs" noWrap>
          {titleOverride ? titleOverride : (
            <div>
              <Text size="md" weight={700}>
                {showHistory && isBelowLarge ? 'Chat History' : 'Captiva AI Assistant'}
              </Text>
              {!isBelowLarge && (
                <Text size="sm" color="dimmed">
                  Ask for trends, anomalies, and operational insights
                </Text>
              )}
            </div>
          )}
        </Group>
        <Group spacing="xs">
          <Button
            variant="default"
            size="sm"
            radius="xl"
            styles={(theme) => ({
              root: { minHeight: '34px', paddingLeft: '12px', paddingRight: '12px' },
              leftIcon: { marginRight: '6px' },
              label: { fontWeight: 500, color: theme.colors.gray[7] }
            })}
            leftIcon={<History size={15} strokeWidth={2} />}
            onClick={() => setShowHistory(!showHistory)}
          >
            {showHistory ? "Hide History" : "Previous Conversations"}
          </Button>
          <Button
            variant="filled"
            color="cap-purple"
            size="sm"
            radius="xl"
            styles={(theme) => ({
              root: { minHeight: '34px', paddingLeft: '14px', paddingRight: '14px' },
              leftIcon: { marginRight: '6px' },
              label: { fontWeight: 500 }
            })}
            leftIcon={<Plus size={15} strokeWidth={2.5} />}
            onClick={() => {
              startNewChat();
              if (isMobile) setShowHistory(false);
            }}
          >
            New Chat
          </Button>
          {activeImageContext && (
            <Tooltip
              label={`Active image context: ${activeImageContext.name || 'Attached photo'}`}
              withArrow
              position="bottom"
            >
              <div
                data-testid="active-image-context-chip"
                onClick={() => {
                  setPreviewImageUrl(activeImageContext.url);
                  setPreviewImageTitle(activeImageContext.name || 'Active Image Context');
                  setPreviewModalOpen(true);
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '3px 8px 3px 4px',
                  borderRadius: '16px',
                  backgroundColor: theme.colors['cap-purple'][0],
                  border: `1px solid ${theme.colors['cap-purple'][3]}`,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
                title="Active Image Context - Click to preview"
              >
                <img
                  src={activeImageContext.url}
                  alt={activeImageContext.name ? `Active context: ${activeImageContext.name}` : 'Active image context'}
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
                <Text
                  size="xs"
                  weight={600}
                  color={theme.colors['cap-purple'][8]}
                  style={{ maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {activeImageContext.name || 'Image'}
                </Text>
                <ActionIcon
                  size="xs"
                  radius="xl"
                  variant="hover"
                  color="gray"
                  aria-label="Clear active image context"
                  onClick={handleClearActiveImage}
                  style={{
                    width: '18px',
                    height: '18px',
                    minWidth: '18px',
                    minHeight: '18px',
                  }}
                >
                  <IconX size={12} />
                </ActionIcon>
              </div>
            </Tooltip>
          )}
          <Badge
            color={isThinking ? 'orange' : 'green'}
            variant="light"
            size="sm"
            leftSection={
              <div
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: isThinking ? theme.colors.orange[6] : theme.colors.green[6],
                }}
              />
            }
          >
            {isThinking ? 'Responding' : 'Ready'}
          </Badge>
          {extraAction}
        </Group>
      </Group>

      <Divider mb="sm" />

      <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* History Sidebar / Drawer */}
        {(() => {
          const historyContent = (
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
              }}
            >
              <Group position="apart" mb="xs">
                <Text size="sm" weight={700} color="dimmed" transform="uppercase">
                  Past Conversations
                </Text>
                <ActionIcon
                  size="sm"
                  variant="transparent"
                  color="gray"
                  onClick={() => setShowHistory(false)}
                  title="Hide previous conversations"
                >
                  <ChevronLeft size={16} />
                </ActionIcon>
              </Group>

              {loadingConversations && conversations.length === 0 ? (
                <Group position="center" py="xl">
                  <Loader size="sm" color="cap-purple" />
                </Group>
              ) : conversations.length === 0 ? (
                <Text size="sm" color="dimmed" align="center" py="xl">
                  No past conversations
                </Text>
              ) : (
                <ScrollArea style={{ flex: 1 }}>
                  <Stack spacing={4}>
                    {conversations.map((conv) => {
                      const isActive = conversationId === conv.id;
                      return (
                        <div
                          key={conv.id}
                          onClick={() => {
                            loadConversation(conv.id);
                            if (mode === 'lite' || isMobile) {
                              setShowHistory(false);
                            }
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            padding: '10px 0',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            backgroundColor: isActive
                              ? theme.colors.gray[1]
                              : 'transparent',
                            transition: 'background-color 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) e.currentTarget.style.backgroundColor = theme.colors.gray[0];
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <div style={{ minWidth: 0, flex: 1, display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <Text
                                size="sm"
                                weight={isActive ? 600 : 400}
                                lineClamp={3}
                                style={{
                                  wordBreak: 'break-word',
                                  color: theme.colors.gray[9],
                                  lineHeight: 1.3,
                                }}
                              >
                                {conv.initialPrompt}
                              </Text>
                              <Text color="dimmed" style={{ fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                {formatDate(conv.updatedAt)}
                              </Text>
                            </div>
                          </div>

                          <Tooltip label="Delete chat" withinPortal>
                            <ActionIcon
                              size="md"
                              color="red"
                              variant="transparent"
                              style={{ flexShrink: 0, marginTop: '2px', marginRight: '12px' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteConversation(e, conv.id);
                              }}
                            >
                              <Trash size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </div>
                      );
                    })}
                  </Stack>
                </ScrollArea>
              )}
            </div>
          );

          if (!showHistory) return null;

          if (mode === 'lite' || isMobile) {
            return (
              <Drawer
                opened={showHistory}
                onClose={() => setShowHistory(false)}
                title={<Text size="lg" weight={700}>Chat History</Text>}
                padding="md"
                size="md"
                position="left"
                zIndex={999999}
              >
                {historyContent}
              </Drawer>
            );
          }

          return (
            <div
              style={{
                width: '250px',
                maxWidth: '33.33%',
                flexShrink: 0,
                borderRight: `1px solid ${theme.colors.gray[3]}`,
                marginRight: '16px',
                paddingRight: '16px',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
              }}
            >
              {historyContent}
            </div>
          );
        })()}

        {/* Main Chat Area */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minWidth: 0,
          }}
        >
            {loadingActiveConversation ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Loader size="sm" mb="xs" color="cap-purple" />
                <Text size="sm" color="dimmed">Loading conversation history...</Text>
              </div>
            ) : (
              <>
                <ScrollArea style={{ flex: 1, paddingRight: '4px' }} viewportRef={scrollAreaRef}>
                  {messages.length === 0 && !isThinking && !input.trim() && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: mode === 'full' ? '8px 0px' : '8px', marginBottom: '16px' }}>
                      {mode === 'lite' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <Text size="sm" color="dimmed" weight={600} transform="uppercase">
                            {getContextualSuggestions().title}
                          </Text>
                          <Stack spacing="xs">
                            {getContextualSuggestions().suggestions.map((item) => (
                              <Card
                                key={item.text}
                                shadow="xs"
                                p="sm"
                                radius="md"
                                withBorder
                                style={{
                                  cursor: 'pointer',
                                  borderColor: theme.colors.gray[2],
                                  transition: 'border-color 0.2s',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.borderColor = theme.colors['cap-purple'][3]}
                                onMouseLeave={(e) => e.currentTarget.style.borderColor = theme.colors.gray[2]}
                                onClick={() => applyQuickPrompt(item.text)}
                              >
                                <Group position="apart" noWrap>
                                  <div style={{ flex: 1 }}>
                                    <Text size="sm" weight={600} style={{ color: theme.colors.gray[8] }}>
                                      {item.text}
                                    </Text>
                                    <Text size="sm" color="dimmed" mt={2}>
                                      {item.desc}
                                    </Text>
                                  </div>
                                  <ActionIcon variant="light" color="cap-purple" radius="xl" size="xs">
                                    <ChevronRight size={14} strokeWidth={2.5} />
                                  </ActionIcon>
                                </Group>
                              </Card>
                            ))}
                          </Stack>
                        </div>
                      ) : (
                        <>

                          <Tabs color="cap-purple" variant="default" style={{ marginBottom: '12px' }}>
                            {/* TAB 1: PREDICTIVE SUGGESTIONS */}
                            <TabItem label="Predictive Prompts">
                              <div style={{ paddingTop: '20px' }}>
                                <Text size="sm" color="dimmed" mb="xs" weight={600} transform="uppercase">
                                  {getContextualSuggestions().title}
                                </Text>
                                <SimpleGrid cols={isBelowLarge ? 1 : 2} spacing="xs">
                                  {getContextualSuggestions().suggestions.map((item) => (
                                    <Card
                                      key={item.text}
                                      shadow="xs"
                                      p="xs"
                                      radius="md"
                                      withBorder
                                      style={{
                                        cursor: 'pointer',
                                        transition: 'transform 0.2s, border-color 0.2s, box-shadow 0.2s',
                                        borderColor: theme.colors.gray[2],
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.borderColor = theme.colors['cap-purple'][3];
                                        e.currentTarget.style.boxShadow = theme.shadows.sm;
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.borderColor = theme.colors.gray[2];
                                        e.currentTarget.style.boxShadow = 'none';
                                      }}
                                      onClick={() => applyQuickPrompt(item.text)}
                                    >
                                      <Group position="apart" noWrap>
                                        <div style={{ flex: 1 }}>
                                          <Text size="sm" weight={600} style={{ color: theme.colors.gray[8] }}>
                                            {item.text}
                                          </Text>
                                          <Text size="sm" color="dimmed" mt={2}>
                                            {item.desc}
                                          </Text>
                                        </div>
                                        <ActionIcon variant="light" color="cap-purple" radius="xl" size="xs">
                                          <ChevronRight size={14} strokeWidth={2.5} />
                                        </ActionIcon>
                                      </Group>
                                    </Card>
                                  ))}
                                </SimpleGrid>

                                {/* Historical Prompts fallbacks */}
                                {conversations.length > 0 && (
                                  <div style={{ marginTop: '14px' }}>
                                    <Text size="sm" color="dimmed" mb="xs" weight={600} transform="uppercase">
                                      From your recent chats
                                    </Text>
                                    <Group spacing="xs">
                                      {conversations.slice(0, 3).map((conv) => (
                                        <Button
                                          key={conv.id}
                                          variant="light"
                                          color="gray"
                                          size="sm"
                                          radius="md"
                                          style={{ minHeight: 34 }}
                                          onClick={() => applyQuickPrompt(conv.initialPrompt)}
                                        >
                                          {conv.initialPrompt.length > 40
                                            ? conv.initialPrompt.substring(0, 40) + '...'
                                            : conv.initialPrompt}
                                        </Button>
                                      ))}
                                    </Group>
                                  </div>
                                )}
                              </div>
                            </TabItem>

                            {/* TAB 2: PROMPT FORMULAS (BEST PRACTICES) */}
                            <TabItem label="Prompt Formulas">
                              <div style={{ paddingTop: '20px' }}>
                                <Text size="sm" color="dimmed" mb="xs">
                                  Use these structured formula templates to get the most accurate answers from the AI. Click to edit in the prompt box:
                                </Text>
                                <Stack spacing="xs">
                                  <Card
                                    p="xs"
                                    radius="md"
                                    withBorder
                                    style={{
                                      cursor: 'pointer',
                                      transition: 'transform 0.2s, border-color 0.2s, box-shadow 0.2s',
                                      borderColor: theme.colors.gray[2],
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.borderColor = theme.colors['cap-purple'][3];
                                      e.currentTarget.style.boxShadow = theme.shadows.sm;
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.borderColor = theme.colors.gray[2];
                                      e.currentTarget.style.boxShadow = 'none';
                                    }}
                                    onClick={() => applyQuickPrompt("Create a [purpose] checklist form template with fields: [field1, field2, ...]")}
                                  >
                                    <Badge size="xs" color="cap-purple" mb={4}>Flex Forms Builder</Badge>
                                    <Text size="sm" weight={700} color="cap-navy">
                                      Create a <Text component="span" color="cap-purple" inherit>[purpose]</Text> checklist form template with fields: <Text component="span" color="cap-purple" inherit>[field1, field2, ...]</Text>
                                    </Text>
                                    <Text size="sm" color="dimmed" mt={4}>
                                      Formula to dynamically build validated mobile inspection forms.
                                    </Text>
                                  </Card>

                                  <Card
                                    p="xs"
                                    radius="md"
                                    withBorder
                                    style={{
                                      cursor: 'pointer',
                                      transition: 'transform 0.2s, border-color 0.2s, box-shadow 0.2s',
                                      borderColor: theme.colors.gray[2],
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.borderColor = theme.colors['cap-purple'][3];
                                      e.currentTarget.style.boxShadow = theme.shadows.sm;
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.borderColor = theme.colors.gray[2];
                                      e.currentTarget.style.boxShadow = 'none';
                                    }}
                                    onClick={() => applyQuickPrompt("Analyze fuel trends for vehicle [Name] comparing to [Asset/Group] during the last 30 days")}
                                  >
                                    <Badge size="xs" color="cap-purple" mb={4}>Fuel Analytics</Badge>
                                    <Text size="sm" weight={700} color="cap-navy">
                                      Analyze fuel trends for vehicle <Text component="span" color="cap-purple" inherit>[Name]</Text> comparing to <Text component="span" color="cap-purple" inherit>[Asset/Group]</Text> during the last 30 days
                                    </Text>
                                    <Text size="sm" color="dimmed" mt={4}>
                                      Formula to scan fuel consumption profiles and pinpoint anomalies.
                                    </Text>
                                  </Card>

                                  <Card
                                    p="xs"
                                    radius="md"
                                    withBorder
                                    style={{
                                      cursor: 'pointer',
                                      transition: 'transform 0.2s, border-color 0.2s, box-shadow 0.2s',
                                      borderColor: theme.colors.gray[2],
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.borderColor = theme.colors['cap-purple'][3];
                                      e.currentTarget.style.boxShadow = theme.shadows.sm;
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.borderColor = theme.colors.gray[2];
                                      e.currentTarget.style.boxShadow = 'none';
                                    }}
                                    onClick={() => applyQuickPrompt("Find delay and route checkpoint exceptions for operator [Name] in trips completed this month")}
                                  >
                                    <Badge size="xs" color="cap-purple" mb={4}>Trip Exceptions Audits</Badge>
                                    <Text size="sm" weight={700} color="cap-navy">
                                      Find delay and route checkpoint exceptions for operator <Text component="span" color="cap-purple" inherit>[Name]</Text> in trips completed this month
                                    </Text>
                                    <Text size="sm" color="dimmed" mt={4}>
                                      Formula to track geofenced checkpoints and timeline variances.
                                    </Text>
                                  </Card>
                                </Stack>
                              </div>
                            </TabItem>

                            {/* TAB 3: FEATURE DISCOVERY */}
                            <TabItem label="Discover Features">
                              <div style={{ paddingTop: '20px' }}>
                                <Text size="sm" color="dimmed" mb="xs">
                                  Discover powerful features available on the Captiva platform that you might not be using yet:
                                </Text>
                                <SimpleGrid cols={isBelowLarge ? 1 : 2} spacing="xs">
                                  <Card
                                    p="xs"
                                    radius="md"
                                    withBorder
                                    style={{
                                      cursor: 'pointer',
                                      transition: 'transform 0.2s, border-color 0.2s, box-shadow 0.2s',
                                      borderColor: theme.colors.gray[2],
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.borderColor = theme.colors['cap-purple'][3];
                                      e.currentTarget.style.boxShadow = theme.shadows.sm;
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.borderColor = theme.colors.gray[2];
                                      e.currentTarget.style.boxShadow = 'none';
                                    }}
                                    onClick={() => applyQuickPrompt("Create a pre-start checklist form template for farm tractors")}
                                  >
                                    <Group position="apart" mb={6} noWrap>
                                      <Text size="sm" weight={700} style={{ color: theme.colors['cap-purple'][6] }}>
                                        Flex Form AI Generation
                                      </Text>
                                      <Badge size="xs" color="cap-purple">Flex</Badge>
                                    </Group>
                                    <Text size="sm" color="dimmed">
                                      Generate validated inspect templates on the fly for your team's mobile devices.
                                    </Text>
                                    <Group position="apart" mt="xs">
                                      <Text size="sm" weight={600} color="cap-purple">
                                        Try form builder prompt
                                      </Text>
                                      <ActionIcon variant="light" color="cap-purple" radius="xl" size="xs">
                                        <ChevronRight size={12} />
                                      </ActionIcon>
                                    </Group>
                                  </Card>

                                  <Card
                                    p="xs"
                                    radius="md"
                                    withBorder
                                    style={{
                                      cursor: 'pointer',
                                      transition: 'transform 0.2s, border-color 0.2s, box-shadow 0.2s',
                                      borderColor: theme.colors.gray[2],
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.borderColor = theme.colors['cap-navy'][3];
                                      e.currentTarget.style.boxShadow = theme.shadows.sm;
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.borderColor = theme.colors.gray[2];
                                      e.currentTarget.style.boxShadow = 'none';
                                    }}
                                    onClick={() => applyQuickPrompt("Create a new vehicle: Scania Horse, White 2023, ODO 12000 km")}
                                  >
                                    <Group position="apart" mb={6} noWrap>
                                      <Text size="sm" weight={700} style={{ color: theme.colors['cap-navy'][6] }}>
                                        Edit Fleet Assets
                                      </Text>
                                      <Badge size="xs" color="cap-navy" variant="filled">Assets</Badge>
                                    </Group>
                                    <Text size="sm" color="dimmed">
                                      Onboard or modify vehicles, onsite tanks, and operators using natural chat.
                                    </Text>
                                    <Group position="apart" mt="xs">
                                      <Text size="sm" weight={600} color="cap-navy">
                                        Try asset creation
                                      </Text>
                                      <ActionIcon variant="light" color="cap-navy" radius="xl" size="xs">
                                        <ChevronRight size={12} />
                                      </ActionIcon>
                                    </Group>
                                  </Card>

                                  <Card
                                    p="xs"
                                    radius="md"
                                    withBorder
                                    style={{
                                      cursor: 'pointer',
                                      transition: 'transform 0.2s, border-color 0.2s, box-shadow 0.2s',
                                      borderColor: theme.colors.gray[2],
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.borderColor = theme.colors['cap-purple'][3];
                                      e.currentTarget.style.boxShadow = theme.shadows.sm;
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.borderColor = theme.colors.gray[2];
                                      e.currentTarget.style.boxShadow = 'none';
                                    }}
                                    onClick={() => applyQuickPrompt("What anomalies did OCR verification find on our fuel transactions recently?")}
                                  >
                                    <Group position="apart" mb={6} noWrap>
                                      <Text size="sm" weight={700} style={{ color: theme.colors['cap-purple'][6] }}>
                                        Receipt OCR & verification
                                      </Text>
                                      <Badge size="xs" color="cap-purple">Fuel AI</Badge>
                                    </Group>
                                    <Text size="sm" color="dimmed">
                                      Automatic transaction verification using Vision OCR and Gemini 2.5 Flash.
                                    </Text>
                                    <Group position="apart" mt="xs">
                                      <Text size="sm" weight={600} color="cap-purple">
                                        Ask about OCR verifications
                                      </Text>
                                      <ActionIcon variant="light" color="cap-purple" radius="xl" size="xs">
                                        <ChevronRight size={12} />
                                      </ActionIcon>
                                    </Group>
                                  </Card>
                                </SimpleGrid>
                              </div>
                            </TabItem>
                          </Tabs>
                        </>
                      )}
                    </div>
                  )}

                  {messages.map((msg, index) => {
                    const isUser = msg.role === 'user';

                    // Suggestion card
                    if (msg.type === 'suggestion') {
                      const fieldEntries = Object.entries(msg.data || {});
                      const hasFieldsArray = Array.isArray(msg.data?.fields);
                      const summaryEntries = hasFieldsArray
                        ? fieldEntries.filter(([key]) => key !== 'fields')
                        : fieldEntries;
                      const skillLabel = (msg.skill || '')
                        .replace(/([A-Z])/g, ' $1')
                        .replace(/^./, (s) => s.toUpperCase())
                        .trim();
                      return (
                        <div
                          key={`${msg.createdAt || index}-${index}`}
                          style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '10px' }}
                        >
                          <Paper
                            p="sm"
                            style={{
                              maxWidth: '88%',
                              background: theme.colors.gray[0],
                              border: `1px solid ${theme.colors['cap-purple'][2]}`,
                              borderRadius: '12px',
                            }}
                          >
                            <Group position="apart" mb="xs">
                              <Text size="sm" color="dimmed">AI Suggestion</Text>
                              <Badge size="xs" color="cap-purple" variant="light">{skillLabel}</Badge>
                            </Group>
                            <Text size="sm" mb="xs">{msg.reasoning}</Text>
                            <Table striped mb="sm" style={{ fontSize: '14px' }}>
                              <thead>
                                <tr>
                                  <th>Field</th>
                                  <th>Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                {summaryEntries.map(([key, value]) => {
                                  const isChanged = msg.changedFields?.includes(key);
                                  return (
                                    <tr key={key}>
                                      <td>
                                        <Text weight={isChanged ? 700 : 400} size="xs">
                                          {humanizeLabel(key)}
                                          {isChanged && <Badge size="xs" color="cap-orange" variant="light" ml={4}>changed</Badge>}
                                        </Text>
                                      </td>
                                      <td>{renderSuggestionValue(value)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                            {hasFieldsArray && msg.data?.fields && (
                              <div style={{ marginBottom: '12px' }}>
                                <Text size="sm" weight={700} mb={6}>Fields</Text>
                                {renderFieldsTable(msg.data.fields)}
                              </div>
                            )}
                            <Group spacing="xs">
                              {msg.actionTaken ? (
                                <>
                                  <Button
                                    size="sm"
                                    color={msg.actionTaken === 'accepted' ? 'cap-green' : 'gray'}
                                    style={{ minHeight: 34 }}
                                    disabled
                                  >
                                    {msg.actionTaken === 'accepted' ? 'Accepted' : 'Rejected'}
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    color="cap-green"
                                    style={{ minHeight: 34 }}
                                    onClick={() => handleConfirm(msg)}
                                    disabled={isThinking}
                                  >
                                    Accept
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="default"
                                    color="gray"
                                    style={{ minHeight: 34 }}
                                    onClick={() => handleReject(msg)}
                                    disabled={isThinking}
                                  >
                                    Reject
                                  </Button>
                                </>
                              )}
                            </Group>
                          </Paper>
                        </div>
                      );
                    }

                    // Clarification message with optional quick-reply buttons
                    if (msg.type === 'clarification') {
                      return (
                        <div
                          key={`${msg.createdAt || index}-${index}`}
                          style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '10px' }}
                        >
                          <div
                            style={{
                              maxWidth: '82%',
                              background: aiBubbleBg,
                              border: `1px solid ${aiBubbleBorder}`,
                              borderRadius: '12px',
                              padding: '10px 12px',
                            }}
                          >
                            <Text size="sm" color="dimmed" mb={4}>AI</Text>
                            <Text size="sm">{msg.text}</Text>
                            {msg.options && msg.options.length > 0 && (
                              <Group spacing="xs" mt="sm">
                                {msg.options.map((opt) => (
                                  <Button
                                    key={opt}
                                    size="sm"
                                    variant="outline"
                                    color="cap-blue"
                                    style={{ minHeight: 34 }}
                                    onClick={() => handleSend(opt)}
                                    disabled={isThinking}
                                  >
                                    {opt}
                                  </Button>
                                ))}
                              </Group>
                            )}
                          </div>
                        </div>
                      );
                    }

                    // Standard text bubble (user or answer/error)
                    return (
                      <div
                        key={`${msg.createdAt || index}-${index}`}
                        style={{
                          display: 'flex',
                          justifyContent: isUser ? 'flex-end' : 'flex-start',
                          marginBottom: '10px',
                        }}
                      >
                        <div
                          style={{
                            maxWidth: '82%',
                            background: isUser ? userBubbleBg : aiBubbleBg,
                            border: `1px solid ${isUser ? userBubbleBorder : aiBubbleBorder}`,
                            borderRadius: '12px',
                            padding: '10px 12px',
                          }}
                        >
                          <Text size="sm" color="dimmed" mb={4}>
                            {isUser ? 'You' : 'AI'}
                          </Text>
                          {isUser && msg.attachments && msg.attachments.length > 0 && (
                            <div style={{ marginTop: '4px', marginBottom: '8px' }}>
                              {msg.attachments.map((att, idx) => (
                                <div
                                  key={att.storagePath || att.url || idx}
                                  onClick={() => {
                                    setPreviewImageUrl(att.url);
                                    setPreviewImageTitle(att.name || 'Photo Attachment');
                                    setPreviewModalOpen(true);
                                  }}
                                  style={{
                                    maxWidth: '200px',
                                    maxHeight: '150px',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    border: `1px solid ${theme.colors['cap-purple'][2]}`,
                                    cursor: 'pointer',
                                    boxShadow: theme.shadows.xs,
                                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                                  }}
                                  title="Click to expand"
                                >
                                  <img
                                    src={att.url}
                                    alt={att.name || 'Attached photo'}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      maxHeight: '150px',
                                      objectFit: 'cover',
                                      display: 'block',
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                          <div style={{ fontSize: '15px', lineHeight: 1.5, overflowX: 'auto' }}>
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                a: ({ href, children, ...props }) => {
                                  const isInternal = Boolean(href && href.startsWith('/'));
                                  if (isInternal) {
                                    return (
                                      <a
                                        href={href}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          if (href) navigate(href);
                                        }}
                                        style={{
                                          color: theme.colors['cap-purple'][6],
                                          fontWeight: 600,
                                          textDecoration: 'underline',
                                          cursor: 'pointer',
                                        }}
                                        {...props}
                                      >
                                        {children}
                                      </a>
                                    );
                                  }
                                  return (
                                    <a
                                      href={href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        color: theme.colors['cap-purple'][6],
                                        fontWeight: 600,
                                        textDecoration: 'underline',
                                      }}
                                      {...props}
                                    >
                                      {children}
                                    </a>
                                  );
                                },
                              }}
                            >
                              {msg.text || ''}
                            </ReactMarkdown>
                          </div>
                          {!isUser && Array.isArray(msg.citations) && msg.citations.length > 0 && (
                            <div style={{ marginTop: '10px' }}>
                              <Text size="sm" color="dimmed" mb={4}>
                                Sources
                              </Text>
                              <Stack spacing={6}>
                                {msg.citations.map((citation) => {
                                  const hasImage = !!citation.imageUrl;
                                  return (
                                    <div key={citation.id || citation.href} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <a
                                        href={citation.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ fontSize: '12px', color: theme.colors['cap-blue'][7], textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                      >
                                        {hasImage ? '🖼️ ' : ''}{citation.label}
                                      </a>
                                      {hasImage && (
                                        <div style={{ marginTop: '4px', maxWidth: '200px', borderRadius: '4px', overflow: 'hidden', border: `1px solid ${theme.colors.gray[3]}` }}>
                                          <a href={citation.href} target="_blank" rel="noopener noreferrer">
                                            <img
                                              src={citation.imageUrl}
                                              alt={citation.label}
                                              style={{
                                                width: '100%',
                                                maxHeight: '120px',
                                                objectFit: 'cover',
                                                display: 'block',
                                                cursor: 'pointer',
                                                transition: 'opacity 0.2s',
                                              }}
                                              onMouseOver={(e) => (e.currentTarget.style.opacity = '0.8')}
                                              onMouseOut={(e) => (e.currentTarget.style.opacity = '1')}
                                            />
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </Stack>
                            </div>
                          )}
                          {!isUser && msg.actionAssetId && (
                            <div style={{ marginTop: '12px' }}>
                              <Button
                                size="sm"
                                variant="outline"
                                color="cap-purple"
                                style={{ minHeight: 34 }}
                                component="a"
                                href={
                                  (() => {
                                    const d = msg.domain || 'asset';
                                    if (d === 'formTemplate') return `/dashboard/form-templates?editId=${msg.actionAssetId}`;
                                    if (d === 'trip') return `/dashboard/trips?tripId=${msg.actionAssetId}&mode=view`;
                                    if (d === 'user') return `/dashboard/users?editId=${msg.actionAssetId}`;
                                    return `/dashboard/assets?editId=${msg.actionAssetId}&domain=${d}`;
                                  })()
                                }
                                target="_blank"
                              >
                                View Asset
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {isThinking && (
                    <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '10px' }}>
                      <div
                        style={{
                          maxWidth: '82%',
                          background: aiBubbleBg,
                          border: `1px solid ${aiBubbleBorder}`,
                          borderRadius: '12px',
                          padding: '10px 12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <Loader size="xs" color="cap-purple" />
                        <Text size="sm" color="dimmed">
                          AI is thinking...
                        </Text>
                      </div>
                    </div>
                  )}
                </ScrollArea>

                {error && (
                  <Alert color="red" mt="sm">
                    <Group position="apart" spacing="xs">
                      <Text size="sm">{error}</Text>
                      <Button variant="white" color="red" compact onClick={handleRetry} disabled={isThinking || !lastSubmittedPrompt}>
                        Retry
                      </Button>
                    </Group>
                  </Alert>
                )}

                <Group position="center" spacing={6} mb={6} mt="xs">
                  <Text size="xs" color="dimmed">
                    AI-generated, verify before acting
                  </Text>
                  <Text size="xs" color="dimmed">•</Text>
                  <Text
                    size="xs"
                    color="dimmed"
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => setShowHistory(!showHistory)}
                    onMouseEnter={(e) => e.currentTarget.style.color = theme.colors['cap-purple'][6]}
                    onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.gray[6]}
                  >
                    {showHistory ? "Hide previous conversations" : "See previous conversations"}
                  </Text>
                </Group>

                {attachmentError && (
                  <Alert color="red" mb="xs" withCloseButton onClose={() => setAttachmentError(null)}>
                    <Text size="sm">{attachmentError}</Text>
                  </Alert>
                )}

                {stagedAttachment && (
                  <Paper
                    p={8}
                    mb={8}
                    radius="md"
                    withBorder
                    style={{
                      backgroundColor: theme.colors.gray[0],
                      borderColor: theme.colors.gray[3],
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '10px',
                      maxWidth: '100%',
                    }}
                  >
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 6,
                        overflow: 'hidden',
                        border: `1px solid ${theme.colors.gray[3]}`,
                        flexShrink: 0,
                        backgroundColor: '#fff',
                      }}
                    >
                      <img
                        src={stagedAttachment.previewUrl}
                        alt={stagedAttachment.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    </div>

                    <div style={{ minWidth: 0, flex: 1 }}>
                      <Group spacing={6} noWrap>
                        <Text size="sm" weight={600} lineClamp={1} style={{ wordBreak: 'break-all' }}>
                          {stagedAttachment.name}
                        </Text>
                        {stagedAttachment.wasDownscaled && (
                          <Badge size="xs" color="teal" variant="light">
                            Downscaled
                          </Badge>
                        )}
                      </Group>
                      <Text size="xs" color="dimmed">
                        {formatFileSize(stagedAttachment.compressedSize)}
                        {stagedAttachment.wasDownscaled && ` (from ${formatFileSize(stagedAttachment.originalSize)})`}
                      </Text>
                    </div>

                    <ActionIcon
                      size="sm"
                      color="red"
                      variant="hover"
                      aria-label="Remove attachment"
                      onClick={handleRemoveAttachment}
                      disabled={isThinking || isUploading}
                    >
                      <IconX size={16} />
                    </ActionIcon>
                  </Paper>
                )}

                <Group align="flex-end" spacing="xs" noWrap mt="xs">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                  />
                  <ActionIcon
                    size={42}
                    radius="md"
                    variant="default"
                    onClick={() => fileInputRef.current?.click()}
                    aria-label="Attach photo"
                    disabled={isThinking || isUploading}
                    style={{ height: 42, width: 42, flexShrink: 0 }}
                  >
                    <IconPhoto size={20} />
                  </ActionIcon>

                  <Textarea
                    placeholder="What are our recent fuel consumption trends?"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    autosize
                    minRows={1}
                    maxRows={4}
                    style={{ flex: 1 }}
                    styles={(theme) => ({
                      input: {
                        borderColor: theme.colors.gray[4],
                        '&:focus': {
                          boxShadow: `inset 0 1px 3px ${theme.colors.gray[2]}`,
                          borderColor: theme.colors['cap-purple'][4]
                        }
                      }
                    })}
                    disabled={isThinking || isUploading}
                    aria-label="Ask the AI assistant"
                  />
                  <Button
                    onClick={() => handleSend()}
                    disabled={isThinking || isUploading || (!input.trim() && !stagedAttachment)}
                    color="cap-purple"
                    aria-label="Send message"
                    rightIcon={<Send size={14} />}
                    size="md"
                    style={{ height: 42 }}
                  >
                    Send
                  </Button>
                </Group>
              </>
            )}
          </div>
      </div>

      <Modal
        opened={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        size="lg"
        centered
        title={
          <Text size="md" weight={700}>
            {previewImageTitle || 'Photo Preview'}
          </Text>
        }
        styles={{
          modal: {
            maxWidth: '90vw',
          },
          body: {
            padding: '12px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#1a1b1e',
            borderRadius: '8px',
            overflow: 'hidden',
          },
        }}
      >
        <div
          style={{
            maxHeight: '75vh',
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <img
            src={previewImageUrl}
            alt={previewImageTitle || 'Photo Preview'}
            style={{
              maxWidth: '100%',
              maxHeight: '75vh',
              objectFit: 'contain',
              borderRadius: '4px',
            }}
          />
        </div>
      </Modal>
    </Paper>
  );
};

export default ChatBox;